import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { fetchPeriods } from '../performance-operations/config';
import { Grid, PageEmpty } from '../performance-operations/styles';
import SummaryPanels from './components/SummaryPanels';
import MeetingsTable from './components/MeetingsTable';
import SurveyGroups from './components/SurveyGroups';
import {
  Content, Fixed, Note, PageWrap,
} from './styles';

// PM analitik P7b — E19 Görüşme ve Süreç Deneyimi (/performance/experience). İK kiracıyı, yönetici alt ağacını görür (uçlar
// kapsamı uygular; ikisi de değilse 403). Sekmeler: Özet (M63–M69 + aşama + anonim anket), Görüşmeler (iş listesi; yönetici
// doğrudan astının görüşmesini kaydeder), Anket Grupları (anonim soru × grup, k gizleme). Süzgeçler URL'dedir.
export default function PerformanceExperiencePage() {
  const { t } = useTranslation();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr, user } = useAuth();
  const canSee = isHr || Boolean(user?.hasSubordinates);
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [tab, setTab] = useState('summary');
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!canSee) return undefined;
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, [canSee]);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMeta(null); }, [filterKey]);
  const onMeta = useCallback((m) => setMeta(m), []);

  const tabs = useMemo(() => [
    { key: 'summary', label: t('performance.experience.tabs.summary') },
    { key: 'meetings', label: t('performance.experience.tabs.meetings') },
    { key: 'groups', label: t('performance.experience.tabs.groups') },
  ], [t]);

  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  return (
    <PageWrap>
      <Fixed>
        <Breadcrumbs items={[{ label: t('performance.experience.title') }]} />
        {!canSee ? <PageEmpty data-state="no-access">{t('performance.experience.noAccess')}</PageEmpty> : null}
        {canSee && periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.experience.noPeriods')}</PageEmpty> : null}
        {canSee && periods.length > 0 ? (
          <PmAnalyticsFilterBar
            periods={periods}
            filters={filters}
            onChange={setFilters}
            onReset={reset}
            lastUpdated={meta?.calculatedAt || null}
            stale={!!meta?.stale}
            groupMembership={filters.groupIds.length ? 'current' : null}
          />
        ) : null}
        {ready ? <Note>{t('performance.experience.principle')}</Note> : null}
        {ready ? <Tabs tabs={tabs} activeTab={tab} onChange={setTab} /> : null}
      </Fixed>
      {ready ? (
        <Content $scroll={tab === 'summary'}>
          {tab === 'summary' ? (
            <Grid key={filters.periodId}>
              <SummaryPanels filters={filters} onMeta={onMeta} />
            </Grid>
          ) : null}
          {tab === 'meetings' ? <MeetingsTable filters={filters} isHr={isHr} /> : null}
          {tab === 'groups' ? <SurveyGroups filters={filters} /> : null}
        </Content>
      ) : null}
    </PageWrap>
  );
}
